import { useParams, Link, useNavigate } from "react-router-dom"
import { useState, useRef, useCallback, useEffect } from "react"
import { motion } from "framer-motion"
import Navbar from "@/components/Navbar"
import Footer from "@/components/Footer"
import ReviewsCarousel from "@/components/ReviewsCarousel"
import WerkplaatsContactSection from "@/components/WerkplaatsContactSection"
import { expertiseData } from "@/data/expertise"
import { expertiseDetails } from "@/data/expertiseDetails"

const expertiseFeatures: Record<string, { title: string; description: string }[]> = {
  "ppf-wrap": [
    { title: "Premium Bescherming", description: "Onze Paint Protection Film biedt de beste bescherming tegen steenslag, krassen en andere beschadigingen." },
    { title: "Zelfhelend", description: "De film heeft zelfhelende eigenschappen bij lichte krassen en beschadigingen." },
    { title: "Garantie", description: "10 jaar garantie op verkleuring en materiaal." },
  ],
  "chiptuning": [
    { title: "Stage 1-3 Tuning", description: "Professionele chiptuning op maat voor optimale prestaties en betrouwbaarheid." },
    { title: "Dyno Tested", description: "Alle tuning wordt getest en verfijnd op onze moderne rollenbank." },
    { title: "OEM+ Kwaliteit", description: "Behoud van fabrieksbetrouwbaarheid met verbeterde prestaties." },
  ],
  "modificaties": [
    { title: "Custom Upgrades", description: "Van subtiele verbeteringen tot complete transformaties." },
    { title: "Premium Onderdelen", description: "Samenwerking met de beste merken in de industrie." },
    { title: "Maatwerk", description: "Elke modificatie wordt specifiek afgestemd op uw wensen en voertuig." },
  ],
  "tuning-kits": [
    { title: "Complete Pakketten", description: "Vooraf samengestelde tuning pakketten voor optimale prestaties." },
    { title: "Plug & Play", description: "Eenvoudige installatie zonder permanente aanpassingen." },
    { title: "Gecertificeerd", description: "Alle kits zijn TÜV-gecertificeerd voor maximale betrouwbaarheid." },
  ],
  "uitlaatsysteem": [
    { title: "Custom Systemen", description: "Op maat gemaakte uitlaatsystemen." },
    { title: "Sound Design", description: "Perfecte geluidsafstemming voor uw voertuig." },
    { title: "Performance", description: "Optimale doorstroming voor betere prestaties." },
  ],
  "onderhoud": [
    { title: "Premium Service", description: "Specialistisch onderhoud voor exclusieve voertuigen." },
    { title: "Originele Onderdelen", description: "Gebruik van hoogwaardige OEM en aftermarket delen." },
    { title: "Diagnose", description: "Geavanceerde diagnostische apparatuur." },
  ],
  "wrappen": [
    { title: "Premium Wrapping", description: "Hoogwaardige carwraps voor complete transformatie." },
    { title: "Custom Design", description: "Unieke designs en kleurencombinaties mogelijk." },
    { title: "Garantie", description: "5 jaar garantie op materiaal en installatie." },
  ],
  "glascoating": [
    { title: "Ceramic Coating", description: "Langdurige bescherming met keramische coating." },
    { title: "Hydrofobisch Effect", description: "Superieure water- en vuilafstoting." },
    { title: "UV-Bescherming", description: "Bescherming tegen verkleuring en oxidatie." },
  ],
  "blinderen": [
    { title: "Premium Folie", description: "Hoogwaardige folies met UV-bescherming." },
    { title: "Certificering", description: "Alle blindering volgens wettelijke normen." },
    { title: "Garantie", description: "Levenslange garantie op verkleuring." },
  ],
  "restyle": [
    { title: "Custom Design", description: "Unieke styling op maat voor uw voertuig." },
    { title: "OEM+ Kwaliteit", description: "Perfecte integratie met originele styling." },
    { title: "3D Visualisatie", description: "Vooraf zien hoe uw auto eruit komt te zien." },
  ],
  "verkoop": [
    { title: "Premium Selectie", description: "Zorgvuldig geselecteerde premium voertuigen." },
    { title: "Uitgebreide Historie", description: "Volledige documentatie en onderhoudshistorie." },
    { title: "Garantie", description: "Uitgebreide garantiemogelijkheden voor zorgeloos rijplezier." },
  ],
  "transport": [
    { title: "Gesloten Transport", description: "Veilig en discreet transport in gesloten transporters." },
    { title: "Verzekerd Vervoer", description: "Volledig verzekerd transport van deur tot deur." },
    { title: "Internationale Service", description: "Transport door heel Europa mogelijk." },
  ],
  "autohotel": [
    { title: "24/7 Beveiliging", description: "Continue bewaking en klimaatbeheersing voor uw kostbare voertuig." },
    { title: "Klimaatcontrole", description: "Optimale omstandigheden voor langdurige stalling." },
    { title: "Onderhoud Service", description: "Regelmatige controles en onderhoud tijdens stalling." },
  ],
  "bandenhotel": [
    { title: "Professionele Opslag", description: "Klimaatgecontroleerde opslag van uw banden en velgen." },
    { title: "Seizoensservice", description: "Complete wissel- en opslagservice voor zomer- en winterbanden." },
    { title: "Inspectie & Onderhoud", description: "Regelmatige controle van uw opgeslagen banden." },
  ],
  "community": [
    { title: "Exclusieve Events", description: "Toegang tot besloten Vermado evenementen." },
    { title: "Netwerk", description: "Connectie met gelijkgestemde auto-enthousiastelingen." },
    { title: "Privileges", description: "Speciale voordelen voor community leden." },
  ],
  "shop": [
    { title: "Premium Producten", description: "Exclusieve selectie van hoogwaardige automotive producten." },
    { title: "Vakkundig Advies", description: "Persoonlijk advies van onze productspecialisten." },
    { title: "Maatwerk", description: "Custom orders en speciale bestellingen mogelijk." },
  ],
}

export default function ExpertiseDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const details = expertiseDetails[id as string]
  const expertiseInfo = expertiseData.find(exp => exp.id === id)

  const sectionRef = useRef<HTMLDivElement>(null)
  const [scrollProgress, setScrollProgress] = useState(0)
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  const handleScroll = useCallback(() => {
    if (!sectionRef.current) return
    const rect = sectionRef.current.getBoundingClientRect()
    const sectionHeight = sectionRef.current.offsetHeight
    const triggerEnd = sectionHeight * 0.6
    const scrolled = Math.max(0, -rect.top)
    const progress = Math.min(1, Math.max(0, scrolled / triggerEnd))
    setScrollProgress(progress)
  }, [])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  if (!details) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex-1 flex items-center justify-center py-40">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>Expertise niet gevonden</h1>
            <Link to="/expertises" className="text-[#B1A06D] underline underline-offset-4">Terug naar overzicht</Link>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  const basePadding = 16
  const currentPadding = basePadding * (1 - scrollProgress)
  const currentRadius = 24 * (1 - scrollProgress)
  const contentOpacity = 1 - scrollProgress * 1.5

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero — homepage style with image */}
      <section ref={sectionRef} className="pt-[82px]" style={{ paddingBottom: `${Math.max(0, 8 * (1 - scrollProgress))}px` }}>
        <div
          className="relative w-full mx-auto min-h-[400px] md:min-h-[600px] xl:min-h-[700px] overflow-hidden transition-[border-radius] duration-100"
          style={{
            height: 'calc(100vh - 120px)',
            marginLeft: `${currentPadding}px`,
            marginRight: `${currentPadding}px`,
            width: `calc(100% - ${currentPadding * 2}px)`,
            borderRadius: `${currentRadius}px`,
          }}
        >
          <motion.div
            initial={{ scale: 1.05 }}
            animate={{ scale: 1 }}
            transition={{ duration: 10, ease: 'easeOut' }}
            className="absolute inset-0"
          >
            <img
              src={expertiseInfo?.image || details.image}
              className="w-full h-full object-cover"
              alt={details.title}
            />
          </motion.div>

          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

          <div
            className="relative z-20 h-full flex flex-col justify-end pb-8 md:pb-12 lg:pb-16 px-8 md:px-12 lg:px-16"
            style={{ opacity: Math.max(0, contentOpacity) }}
          >
            <div className="max-w-xl">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-white/50 text-xs tracking-[0.15em] uppercase mb-4"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
{details.subtitle}
              </motion.p>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-3 md:mb-6 leading-tight uppercase tracking-[0.04em]"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
{details.title}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-white/90 text-base md:text-lg mb-4 max-w-lg leading-relaxed"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
{details.description}
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="text-white text-lg md:text-xl mb-8"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Prestatie geeft{' '}
                <span className="relative inline-block">
                  <span className="absolute inset-0 bg-[#B1A06D]" style={{ transform: 'skewX(-4deg) rotate(-2deg)', borderRadius: '4px' }} />
                  <span className="relative z-10 text-black px-2 py-0.5 font-semibold inline-block">vrijheid</span>
                </span>
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex flex-col sm:flex-row sm:items-center gap-6"
              >
                <motion.button
                  onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center gap-2 bg-white text-black px-6 py-4 rounded-xl font-semibold text-sm uppercase tracking-wider hover:bg-white/95 transition-colors"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  Meer informatie
                </motion.button>
                <button
                  onClick={() => navigate('/expertises')}
                  className="flex items-center gap-2 text-white/80 text-sm hover:text-white transition-colors"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  <span>←</span>
                  <span className="font-medium">Alle expertises</span>
                </button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Diensten & Proces — domogo 2 column */}
      <section className="bg-white py-20 md:py-28 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-8 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Diensten */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2
                className="text-black text-2xl md:text-3xl font-medium uppercase tracking-[0.08em] mb-10"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                DIENSTEN
              </h2>
              <div className="space-y-0">
                {(details.benefits || []).map((benefit: string, i: number) => (
                  <div key={i} className="flex items-start py-4 border-t border-black/10">
                    <span className="text-[#B1A06D] mr-3 mt-0.5 text-sm">✓</span>
                    <span className="text-black text-sm leading-[1.7]">{benefit}</span>
                  </div>
                ))}
                <div className="border-t border-black/10" />
              </div>
            </motion.div>

            {/* Ons Proces */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h2
                className="text-black text-2xl md:text-3xl font-medium uppercase tracking-[0.08em] mb-10"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                ONS PROCES
              </h2>
              <div className="space-y-0">
                {(details.process || []).map((step: string, i: number) => (
                  <div key={i} className="flex items-start py-4 border-t border-black/10">
                    <span
                      className="text-black/30 mr-4 text-sm font-medium flex-shrink-0 w-6"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="text-black text-sm leading-[1.7]">{step}</span>
                  </div>
                ))}
                <div className="border-t border-black/10" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Statistieken — 4 kolom grid */}
      <section className="bg-[#f2f2f0] py-20 md:py-28 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-8 md:px-12 lg:px-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 lg:gap-8">
            {[
              { key: 'projects', value: `${details.statistics.projectsCompleted}+`, label: 'Projecten voltooid' },
              { key: 'experience', value: `${details.statistics.yearsExperience}`, label: 'Jaar ervaring' },
              { key: 'satisfaction', value: `${details.statistics.satisfactionRate}%`, label: 'Klanttevredenheid' },
              { key: 'clients', value: `${details.statistics.clientsServed}+`, label: 'Tevreden klanten' },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <div className="h-px bg-black/80 mb-7" />
                <p className="text-black text-3xl md:text-4xl font-semibold mb-2">{stat.value}</p>
                <p className="text-black/50 text-[13px] leading-[1.7]">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features — 3 kolom grid */}
      <section className="bg-white py-20 md:py-28 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-8 md:px-12 lg:px-16">
          <h2
            className="text-black text-3xl md:text-4xl lg:text-[2.75rem] font-medium tracking-[0.08em] leading-[1.1] mb-12 md:mb-16 uppercase"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            WAAROM {details.title.toUpperCase()}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12">
            {(expertiseFeatures[id as string] || []).map((feature: any, i: number) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <div className="h-px bg-black/80 mb-7" />
                <h3
                  className="text-black font-semibold text-[15px] md:text-base mb-3 tracking-[0.08em] uppercase"
                  style={{ fontFamily: "'Manrope', sans-serif", lineHeight: 1.3 }}
                >
                  {feature.title}
                </h3>
                <p
                  className="text-black/50 text-[13px] leading-[1.7]"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {details.faq && details.faq.length > 0 && (
        <section className="bg-white py-20 md:py-28 lg:py-32 border-t border-black/5">
          <div className="max-w-[800px] mx-auto px-8 md:px-12 lg:px-16">
            <h2
              className="text-black text-3xl md:text-4xl font-medium tracking-[0.08em] leading-[1.1] uppercase mb-12 md:mb-16"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              VEELGESTELDE VRAGEN
            </h2>
            <div className="space-y-0">
              {details.faq.map((item: any, i: number) => (
                <div key={i} className="border-t border-black/10">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between py-6 text-left"
                  >
                    <span
                      className="text-black text-sm md:text-base font-medium pr-8"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
{item.question}
                    </span>
                    <span
                      className="text-black/40 text-xl flex-shrink-0 transition-transform duration-300"
                      style={{ transform: openFaq === i ? 'rotate(45deg)' : 'rotate(0deg)' }}
                    >
                      +
                    </span>
                  </button>
                  <div
                    className="overflow-hidden transition-all duration-300"
                    style={{ maxHeight: openFaq === i ? '300px' : '0px', opacity: openFaq === i ? 1 : 0 }}
                  >
                    <p
                      className="text-black/50 text-sm leading-[1.75] pb-6"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
{item.answer}
                    </p>
                  </div>
                </div>
              ))}
              <div className="border-t border-black/10" />
            </div>
          </div>
        </section>
      )}

      <WerkplaatsContactSection />

      <ReviewsCarousel />

      <Footer />
    </div>
  )
}
